// Copy for the public landing page. Deliberately short: a title and, at most, one line.
// The screenshots do the explaining.

// Schools, teachers and parents sign in with Google; ?method=google lands straight on that button.
export const GOOGLE_SIGN_IN_URL = '/login?method=google';

// Partners and white-label operators use username + password (and, on their own domains, so do their schools).
export const PARTNER_LOGIN_URL = '/vendor/login';
export const PARTNER_APPLY_URL = '/vendor/register';

export const SUPPORT_URL =
  'https://api.whatsapp.com/send?phone=+919065565303&text=Hello, I need help with VidyalayHelp.';

export const PLATFORM_STATS = [
  { value: '50+', label: 'Schools' },
  { value: '10K+', label: 'Students' },
  { value: '500+', label: 'Staff' },
  { value: '99.9%', label: 'Uptime' },
];

// Product tour: one real screen per tab (keys come from landing-screenshots.json).
export const TOUR_TABS = [
  { id: 'dashboard', label: 'Dashboard', shot: 'dashboard', phone: 'phone-dashboard', url: 'dashboard', title: 'Your whole school at a glance' },
  { id: 'students', label: 'Students', shot: 'students', phone: 'phone-students', url: 'dashboard/ManageStudents', title: 'Every student, one clean record' },
  { id: 'attendance', label: 'Attendance', shot: 'attendance', phone: 'phone-attendance', url: 'dashboard/AttendanceManagement', title: 'Attendance in under a minute' },
  { id: 'exams', label: 'Exams', shot: 'exams', phone: 'phone-exams', url: 'dashboard/ExamManagement', title: 'Every exam, planned in one place' },
  { id: 'results', label: 'Results', shot: 'exam-results', phone: 'phone-exam-results', url: 'dashboard/ExamManagement', title: 'Results and toppers, instantly' },
  { id: 'seating', label: 'Seating', shot: 'exam-seating', phone: 'phone-exam-seating', url: 'dashboard/ExamManagement', title: 'Seating charts that print themselves' },
  { id: 'fees', label: 'Fees', shot: 'fee-dues', phone: 'phone-fee-dues-table', url: 'dashboard/FeeManagement/DuesReport', title: 'Know where every rupee stands' },
  { id: 'admissions', label: 'Admissions', shot: 'enquiries', phone: 'phone-enquiries', url: 'dashboard/EnquiryManagement', title: 'Every enquiry followed up' },
];

// Overview tiles: an illustration and a name. Nothing else.
export const FEATURES = [
  { id: 'students', illustration: 'students', accent: '#2563eb', title: 'Students' },
  { id: 'attendance', illustration: 'attendance', accent: '#10b981', title: 'Attendance' },
  { id: 'exams', illustration: 'exams', accent: '#f59e0b', title: 'Exams & results' },
  { id: 'fees', illustration: 'fees', accent: '#6366f1', title: 'Fees' },
  { id: 'staff', illustration: 'staff', accent: '#8b5cf6', title: 'Staff' },
  { id: 'homework', illustration: 'homework', accent: '#0ea5e9', title: 'Homework' },
  { id: 'inventory', illustration: 'inventory', accent: '#14b8a6', title: 'Inventory' },
  { id: 'enquiries', illustration: 'enquiry', accent: '#ec4899', title: 'Admissions' },
  { id: 'notices', illustration: 'notice', accent: '#f97316', title: 'Notice board' },
  { id: 'notifications', illustration: 'bell', accent: '#ef4444', title: 'Notifications' },
  { id: 'website', illustration: 'website', accent: '#0a63d8', title: 'School website' },
  { id: 'mobile', illustration: 'phone', accent: '#0f766e', title: 'Mobile API' },
];

// Deep dives: the things schools buy a system for, plus admissions.
export const MODULES = [
  {
    id: 'attendance', accent: '#10b981', illustration: 'attendance', tag: 'Attendance',
    title: 'Daily attendance in under a minute',
    text: 'Teachers mark their own sections. You watch the school fill in.',
    points: ['Present, late, half-day, absent', 'Monthly and session reports', 'Spot students below 75%'],
    shots: [{ key: 'attendance', phone: 'phone-attendance', url: 'dashboard/AttendanceManagement' }],
  },
  {
    id: 'exams', accent: '#f59e0b', illustration: 'exams', tag: 'Exams & results',
    title: 'From timetable to report card',
    text: 'Enter marks, see the analysis build itself, print everything for exam day.',
    points: ['Auto grades and pass rate', 'Report cards and admit cards', 'Mixed-class seating plans'],
    shots: [
      { key: 'exam-marks', phone: 'phone-exam-marks', url: 'dashboard/ExamManagement', label: 'Marks' },
      { key: 'exam-results', phone: 'phone-exam-results', url: 'dashboard/ExamManagement', label: 'Results' },
      { key: 'exam-report', phone: 'phone-exam-report', url: 'dashboard/ExamManagement', label: 'Report card' },
      { key: 'exam-seating', phone: 'phone-exam-seating', url: 'dashboard/ExamManagement', label: 'Seating' },
    ],
  },
  {
    id: 'fees', accent: '#6366f1', illustration: 'fees', tag: 'Fees',
    title: 'Track every rupee, effortlessly',
    text: 'A proper ledger behind every student, and a clear view of what is outstanding.',
    points: ['Monthly dues and part-payments', 'Receipts with unique numbers', 'Class-wise dues in Excel'],
    shots: [{ key: 'fee-dues', phone: 'phone-fee-dues-table', url: 'dashboard/FeeManagement/DuesReport' }],
  },
  {
    id: 'students', accent: '#2563eb', illustration: 'students', tag: 'Students',
    title: 'Every student, one clean record',
    text: 'Family, transport and previous school, searchable in a blink.',
    points: ['Bulk Excel upload', 'Class and section rosters', 'Auto-generated logins'],
    shots: [{ key: 'students', phone: 'phone-students', url: 'dashboard/ManageStudents' }],
  },
  {
    id: 'enquiries', accent: '#ec4899', illustration: 'enquiry', tag: 'Admissions',
    title: 'Never lose an admission lead',
    text: 'One form on your website. Every enquiry tracked until admitted.',
    points: ['Embeddable enquiry form', 'Pending to admitted pipeline', 'Excel and PDF export'],
    shots: [{ key: 'enquiries', phone: 'phone-enquiries', url: 'dashboard/EnquiryManagement' }],
  },
];

export const HOW_IT_WORKS = [
  { step: '01', icon: 'google', title: 'Sign in with Google', text: 'No forms, no passwords to remember.' },
  { step: '02', icon: 'school', title: 'Set up your school', text: 'Name, classes and sections.' },
  { step: '03', icon: 'rocket', title: 'Add people and go', text: 'Import from Excel and start.' },
];

// Partner / white-label. Every line maps to a shipped feature: School/Vendor branding, generated
// credentials (Google is hidden on white-label portals), per-school moduleSettings and magic links.
export const PARTNER_POINTS = [
  'Your logo, colours and domain',
  'Username and password for every school',
  'Modules on or off, per school',
  'Secure magic links into any school',
];
